/**
 * InfiniteLooper クラス
 *
 * 指定した要素群（トラック）を無限ループスクロールさせるためのユーティリティ。
 *
 * - 複数のトラック要素に対して、子要素を複製し、ループアニメーションを実現。
 * - ブレークポイントで縦横のループ方向を切り替え可能。
 * - ウィンドウのリサイズやロード時にループサイズを自動調整。
 *
 * @example
 *   new InfiniteLooper('.loop-track', 1024, true);
 *
 * @param {string} selector ループさせるトラック要素のセレクタ
 * @param {number} breakpoint ループ方向を切り替えるウィンドウ幅（px）
 * @param {boolean|number} clone 子要素を複製するか（複製数としても利用）
 *
 * 注意:
 * - トラック内の子要素は複製されるため、動的な内容の場合は注意。
 * - CSSで --loop-size カスタムプロパティを利用してアニメーション制御すること。
 */
export class InfiniteLooper {
	constructor(selector, breakpoint = 768, clone = false) {
		this.selector = selector;
		this.breakpoint = breakpoint;
		this.tracks = [...document.querySelectorAll(selector)];
		this.clone = clone;
		this.init();
	}

	/**
	 * 初期化処理。必要に応じて子要素の複製やイベントリスナー登録を行う。
	 */
	init() {
		if (this.clone) {
			this.tracks.forEach((track) => this.cloneSet(track));
		}

		window.addEventListener('load', () => {
			this.updateLoopSize();
			setTimeout(() => this.updateLoopSize(), 50);
			setTimeout(() => this.updateLoopSize(), 200);
		});

		let timer;
		window.addEventListener('resize', () => {
			clearTimeout(timer);
			timer = setTimeout(() => this.updateLoopSize(), 50);
		});
	}

	/**
	 * トラック内の子要素を複製してループ用セットを作成する。
	 * @param {Element} track 複製対象のトラック要素
	 */
	cloneSet(track) {
		const original = [...track.children];
		track.innerHTML = '';

		for (let i = 0; i < 3; i++) {
			original.forEach((node) => {
				track.appendChild(node.cloneNode(true));
			});
		}
	}

	/**
	 * トラックごとにループサイズ（--loop-size）を計算し、CSSカスタムプロパティとして設定。
	 * ブレークポイントで縦横方向を切り替え。
	 */
	updateLoopSize() {
		const vertical =
			window.innerWidth >= this.breakpoint && this.breakpoint !== -1;

		this.tracks.forEach((track) => {
			const items = [...track.children];
			const setLength = items.length / (this.clone ? this.clone : 1);
			let size = 0;

			for (let i = 0; i < setLength; i++) {
				const rect = items[i].getBoundingClientRect();
				const style = getComputedStyle(items[i]);

				size += vertical
					? rect.height +
					  parseFloat(style.marginTop) +
					  parseFloat(style.marginBottom)
					: rect.width +
					  parseFloat(style.marginLeft) +
					  parseFloat(style.marginRight);
			}

			track.style.setProperty('--loop-size', `${size}px`);
		});
	}
}
